import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthGuard, EmailGuard } from "../App";
import { FaBuilding, FaSearch, FaArrowLeft, FaUser, FaHome, FaCalendarAlt } from 'react-icons/fa';
import { Oval } from "react-loader-spinner"; // Assuming this is available for loading state
import { StandardURL } from "../App";

const AddReservationsBillsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [propertyId, setPropertyId] = useState(
    localStorage.getItem("selectedPropertyId") || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Unified fetch logic
  const fetchReservations = () => {
    const token = localStorage.getItem("token");

    if (!propertyId) {
      setError("Please enter a Property ID to fetch reservations.");
      setReservations([]);
      return;
    }

    if (token) {
      setLoading(true);
      setError(null);
      axios
        .get(
          `${StandardURL}/iNyumba/user/reservations/viewAll/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          const fetchedReservations = response.data.response;
          setReservations(fetchedReservations || []);
          if (!fetchedReservations || fetchedReservations.length === 0) {
              setError(`No active reservations found for Property ID: ${propertyId}`);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching reservations:", err);
          setError("Failed to fetch reservations. Check Property ID and network connection.");
          setReservations([]);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    // Only auto-fetch if propertyId is present on load
    if (propertyId) {
      fetchReservations();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewBills = (roomId) => {
    // Set rental ID (room_id) in local storage
    localStorage.setItem("selectedRentalId", roomId);
    // Redirect to the bills page for the specific rental
    navigate("/admin/addpropertybills");
  };

  const handlePropertyIdChange = (event) => {
    const newPropertyId = event.target.value;
    setPropertyId(newPropertyId);
    localStorage.setItem("selectedPropertyId", newPropertyId); // Store in localStorage
    if (error) setError(null); // Clear error when user starts typing
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto">
            
            {/* Header and Back Button */}
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-4xl font-extrabold text-gray-900 flex items-center">
                    <FaBuilding className="mr-3 text-indigo-600" /> Set Tenant Bills
                </h1>
                <button
                    onClick={() => {
                        setPropertyId(""); 
                        localStorage.removeItem("selectedPropertyId");
                        navigate(-1); 
                    }}
                    className="flex items-center text-gray-600 hover:text-indigo-600 font-semibold transition duration-150 p-2 rounded-lg bg-white shadow-md hover:shadow-lg"
                >
                    <FaArrowLeft className="mr-2" /> Go Back
                </button>
            </div>

            {/* Property ID Input Card */}
            <div className="bg-white shadow-xl rounded-xl p-6 mb-8 border-t-4 border-indigo-500">
              <label htmlFor="propertyId" className="block text-lg font-bold text-gray-700 mb-3">
                1. Enter Property ID
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  id="propertyId"
                  value={propertyId}
                  onChange={handlePropertyIdChange}
                  className="flex-grow border border-gray-300 rounded-lg px-4 py-3 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                  placeholder="e.g., PROP12345. Enter Property ID to load tenants"
                />
                <button
                  onClick={fetchReservations}
                  disabled={loading || !propertyId}
                  className={`flex-shrink-0 flex items-center justify-center font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out ${
                    loading || !propertyId
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  }`}
                >
                  <FaSearch className="mr-2" /> 
                  {loading ? 'Fetching...' : 'Fetch Reservations'}
                </button>
              </div>
              {error && (
                  <p className="mt-4 text-red-500 font-medium">{error}</p>
              )}
            </div>

            {/* Reservations List Section */}
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
                2. Select Tenant to Set Bills
            </h2>
            
            {loading && (
                <div className="flex justify-center items-center py-10">
                  <Oval color="#4f46e5" secondaryColor="#6366f1" height={50} width={50} ariaLabel="loading-spinner" />
                  <p className="ml-4 text-xl text-gray-600">Loading tenants...</p>
                </div>
            )}

            {!loading && reservations.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reservations.map((reservation) => (
                  <div
                    key={reservation.room_id}
                    className="bg-white shadow-lg hover:shadow-xl transition duration-300 rounded-xl p-6 border-l-4 border-green-500 flex flex-col justify-between"
                  >
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                            <FaUser className="mr-2 text-indigo-500" /> {reservation.user_name}
                        </h3>
                        <div className="space-y-2 text-sm text-gray-700">
                            <p>
                                <strong>Email:</strong> {reservation.email}
                            </p>
                            <p>
                                <strong>Phone:</strong> {reservation.phone}
                            </p>
                            <p className="flex items-center">
                                <FaHome className="mr-2 text-gray-400" />
                                <strong>House No:</strong> {reservation.house_no}
                            </p>
                            <p className="flex items-center">
                                <FaCalendarAlt className="mr-2 text-gray-400" />
                                <strong>Entry Date:</strong> {new Date(reservation.entrydate).toLocaleDateString()}
                            </p>
                        </div>

                        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
                            <p>
                                <strong className="font-semibold">Status:</strong>{" "}
                                <span className={`font-medium px-2 py-0.5 rounded-full text-xs ${
                                    reservation.booking_status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {reservation.booking_status}
                                </span>
                            </p>
                            <p>
                                <strong className="font-semibold">Deposit:</strong> {reservation.deposit_paid}
                            </p>
                            <p>
                                <strong className="font-semibold">Payment:</strong>{" "}
                                <span className={`font-medium ${
                                    reservation.payment_status === 'Paid' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                    {reservation.payment_status}
                                </span>
                            </p>
                        </div>
                    </div>
                    
                    {/* Button for viewing bills */}
                    <button
                      onClick={() => handleViewBills(reservation.room_id)}
                      className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg transition duration-150 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Set Tenant Bills
                    </button>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default AddReservationsBillsPage;